// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InfoCard, { fields } from "@mcmc/App/Widgets/InfoCard/InfoCard.component";
import React from "react";

interface InfoCardWidgetProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.imageCaption]: string;
        [fields.copy]: string;
        [fields.link]: LinkFieldValue;
        [fields.linkText]: string;
        [fields.linkAlignment]: "left" | "center" | "right";
        [fields.openInNewTab]: boolean;
        [fields.isSolutionsWidget]: boolean;
    };
}

const InfoCardWidget: React.FC<InfoCardWidgetProps> = ({ fields }) => {
    const { url } = useGetLink(fields.link);

    return <InfoCard {...fields} url={url} direction="vertical" extendedStyles={{ heading: { forwardAs: "h3" } }} />;
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.imageUrl,
            fieldType: "Contextual",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: fields.imageCaption,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: fields.link,
            displayName: "Link",
            editorTemplate: "LinkField",
            defaultValue: { value: "", type: "Page" },
            fieldType: "Contextual",
            isRequired: false,
        },
        {
            name: fields.linkText,
            displayName: "Link Label",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Contextual",
            isRequired: false,
            isVisible: item => {
                return item.fields.link?.value;
            },
        },
        {
            name: fields.linkAlignment,
            displayName: "Link Alignment ",
            editorTemplate: "DropDownField",
            options: [
                {
                    displayName: "Left",
                    value: "left",
                },
                {
                    displayName: "Center",
                    value: "center",
                },
                {
                    displayName: "Right",
                    value: "right",
                },
            ],
            isRequired: false,
            defaultValue: "left",
            fieldType: "Contextual",
            isVisible: item => {
                return item.fields.link?.value;
            },
        },
        {
            name: fields.openInNewTab,
            displayName: "Open in New Tab",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
        {
            name: fields.copy,
            displayName: "Copy",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Contextual",
            isRequired: false,
        },
        {
            name: fields.isSolutionsWidget,
            displayName: "Is Solutions & Services",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: InfoCardWidget,
    definition,
};

export default widgetModule;
